import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import { useAnchoredPopoverPlacement } from '@src/components/common/useAnchoredPopoverPlacement';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import React, { useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import { diffWordsForRedline, summarizeRedline } from './inlinePhraseEdit';
import { REDLINE_ADDED_COLOR, REDLINE_REMOVED_COLOR } from './redlineColors';
import { InlineTextAction } from './translationEditorPrimitives';

import type { AnchoredPopoverPlacement } from '@src/components/common/useAnchoredPopoverPlacement';

export interface InlineEditRedlineBubbleProps {
  /** The phrase span the field lives in; the bubble sits under it. */
  anchorElement: HTMLElement;
  /** The phrase as it is now (what a cancel keeps). */
  before: string;
  /** The field's draft, live. */
  draft: string;
  canRevert: boolean;
  onRevert?: () => void;
}

/**
 * PLAT-11650 — what an in-place edit will change, as the reviewer types: a
 * compact red-line ("~~really like~~ → enjoy") derived from the word diff of the
 * phrase against the draft, and the way out ("Enter to save · Esc to cancel",
 * naming the removal when the draft has been emptied).
 * Portaled to the body and placed by measured height like every anchored
 * popover here (design system: Anchored Popovers). Mousedown inside is stopped
 * so clicking Revert is not the field's "click outside".
 */
export const InlineEditRedlineBubble: React.FC<
  InlineEditRedlineBubbleProps
> = ({ anchorElement, before, draft, canRevert, onRevert }) => {
  const ref = useRef<HTMLDivElement>(null);
  const placement = useAnchoredPopoverPlacement(anchorElement, ref, {
    width: BUBBLE_WIDTH,
    estimatedHeight: BUBBLE_ESTIMATED_HEIGHT,
  });
  const hunks = useMemo(
    () => summarizeRedline(diffWordsForRedline(before, draft)),
    [before, draft],
  );
  // Emptying the field commits a REMOVAL, not an ordinary correction — the
  // phrase goes silent and is struck in the review — so Enter is named for what
  // it will actually do before the reviewer presses it.
  const isRemoval = draft.trim() === '' && before.trim() !== '';

  return ReactDOM.createPortal(
    <Bubble
      ref={ref}
      placement={placement}
      role="status"
      aria-live="polite"
      data-testid="inline-edit-bubble"
      onMouseDown={e => e.stopPropagation()}
    >
      <Redline data-testid="inline-edit-redline">
        {hunks.length === 0 ? (
          <Muted>No changes yet</Muted>
        ) : (
          hunks.map((hunk, i) => (
            <React.Fragment key={i}>
              {i > 0 && <Muted> · </Muted>}
              {hunk.removed.length > 0 && (
                <Removed>{hunk.removed.join(' ')}</Removed>
              )}
              {hunk.removed.length > 0 && hunk.added.length > 0 && (
                <Muted> → </Muted>
              )}
              {hunk.added.length > 0 && hunk.removed.length === 0 && (
                <Muted>→ </Muted>
              )}
              {hunk.added.length > 0 && <Added>{hunk.added.join(' ')}</Added>}
            </React.Fragment>
          ))
        )}
      </Redline>
      <Footer>
        <Hint>{`Enter to ${isRemoval ? 'remove' : 'save'} · Esc to cancel`}</Hint>
        {canRevert && onRevert && (
          <InlineTextAction
            type="button"
            data-testid="inline-edit-revert"
            onClick={onRevert}
          >
            Revert
          </InlineTextAction>
        )}
      </Footer>
    </Bubble>,
    document.body,
  );
};

const BUBBLE_WIDTH = 280;
/** First-paint estimate only — the hook re-places against the real height. */
const BUBBLE_ESTIMATED_HEIGHT = 52;

const Bubble = styled.div.attrs(
  (props: { placement: AnchoredPopoverPlacement }) => props,
)`
  position: fixed;
  left: ${p => p.placement.left}px;
  top: ${p => p.placement.top}px;
  width: ${BUBBLE_WIDTH}px;
  max-width: calc(100vw - 16px);
  z-index: var(
    ${TOP_LAYER_VARS.contextMenu},
    ${CLIP_PREVIEW_Z.translationEditorPopover}
  );
  padding: 6px 10px;
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  box-shadow: var(--elevation-toast);
  font-size: 12px;
  line-height: 1.4;
  color: var(--foreground);
`;

const Redline = styled.div`
  word-break: break-word;
`;

const Removed = styled.span`
  color: ${REDLINE_REMOVED_COLOR};
  text-decoration: line-through;
`;

const Added = styled.span`
  color: ${REDLINE_ADDED_COLOR};
`;

const Muted = styled.span`
  color: var(--text-muted);
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
`;

const Hint = styled.span`
  font-size: 11px;
  color: var(--text-muted);
`;
